/**
 * @file src/local/localAudioConfig.js
 * @description The numbers for the local audio library.
 *
 * A local entry is a file on this machine that the app plays without ever uploading or copying it.
 * What is remembered is the LIBRARY — name, duration, peaks, gain, the orbiter and world it plays
 * with, and where the file last lived — never the bytes. The constants here are the ones a person
 * can see the effect of: the extensions a file must have, and the shape of the peaks the stage
 * draws (the same shape the backend's `audiowaveform` emits, so the same waveform code renders
 * both). The rail is always offered in a collection or inline session; a room and the queue never
 * offer it (see `createCollectionApp`).
 */

/**
 * The scope a file dropped into a `?tracks=` session belongs to — not a collection, but not
 * nothing either. Ours, minted here rather than by the platform, which is why nothing ever asks
 * the platform to name it.
 */
export const INLINE_SCOPE = 'inline';

/** The backend's waveform resolution (`audioProcess.js`: `--pixels-per-second 20 --bits 8`). */
export const LOCAL_PEAKS_PIXELS_PER_SECOND = 20;
export const LOCAL_PEAKS_BITS = 8;

/**
 * Extensions the target browsers can hand to `decodeAudioData`. AIFF is out on purpose (Chrome
 * decodes it inconsistently), which is why the designed toast names it.
 */
export const LOCAL_SUPPORTED_EXTENSIONS = new Set(['wav', 'mp3', 'flac', 'ogg', 'oga', 'opus', 'm4a', 'aac', 'mp4', 'webm', 'weba']);

/** Extensions that are audio but not supported here, named in the skipped toast by kind. */
export const LOCAL_KNOWN_UNSUPPORTED_EXTENSIONS = new Set(['aiff', 'aif', 'aifc', 'wma', 'alac', 'ape', 'dsf', 'dff']);
